'use client';

import { useState } from 'react';
import { ChevronRight } from '@doist/icons/brand';
import { Box, Button, Hidden } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { type ProductUITheme } from 'components/product-ui/product-ui-types';
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section';
import { TextLockUp } from 'components/text-lock-up/text-lock-up';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/dist/CustomEase';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useLocale } from 'next-intl';
import { type Scene, SceneContainer } from './scene-container';
import css from './product-scenes-section.module.css';
gsap.registerPlugin(ScrollTrigger, CustomEase);
type SceneData = {
  id: Scene;
  preheader: string;
  title: string;
  description: string;
  button?: {
    href: string;
    textKey: string;
  };
  theme: ProductUITheme;
};
const sceneData: SceneData[] = [{
  id: 'quickAdd',
  preheader: 'productuiScene1.preheader',
  title: 'productuiScene1.title',
  description: 'productuiScene1.description',
  theme: 'yellow'
}, {
  id: 'todayView',
  preheader: 'productuiScene2.preheader',
  title: 'productuiScene2.title',
  description: 'productuiScene2.description',
  theme: 'seaSalt'
}, {
  id: 'simplifyPlanning',
  preheader: 'productuiScene3.preheaderOrganize',
  title: 'productuiScene3.title',
  description: 'productuiScene3.descriptionSharedSpaces',
  theme: 'red'
}, {
  id: 'getItDone',
  preheader: 'productuiScene4.preheaderOrganize',
  title: 'productuiScene4.title',
  description: 'productuiScene4.descriptionSharedSpaces',
  button: {
    href: '/teams',
    textKey: 'productuiScene4.learnMoreAboutTeams'
  },
  theme: 'green'
}];
export function ProductScenesSection() {
  const [activeScene, setActiveScene] = useState<SceneData>(sceneData[0]);
  return <ScrollSection className="mbe-24 md-mbe-32 md-mbe-48" data-sentry-element="ScrollSection" data-sentry-component="ProductScenesSection" data-sentry-source-file="product-scenes-section.tsx">
            <Box className="md-pbs-96" data-sentry-element="Box" data-sentry-source-file="product-scenes-section.tsx">
                <ScrollSection.StaticSection data-sentry-element="unknown" data-sentry-source-file="product-scenes-section.tsx">
                    <Box className={css.sceneContainer} data-sentry-element="Box" data-sentry-source-file="product-scenes-section.tsx">
                        {sceneData.map(scene => <ProductUIThemeProvider theme={scene.theme} key={scene.id}>
                                <ProductSection {...scene} fullHeight={scene.id !== 'getItDone'} anchor={scene.id === 'getItDone'} button={scene.button} onEnter={() => setActiveScene(scene)} onEnterBack={() => setActiveScene(scene)} />
                            </ProductUIThemeProvider>)}
                    </Box>
                </ScrollSection.StaticSection>
            </Box>
            <ScrollSection.StickySection data-sentry-element="unknown" data-sentry-source-file="product-scenes-section.tsx">
                <ProductUIThemeProvider theme={activeScene.theme} data-sentry-element="ProductUIThemeProvider" data-sentry-source-file="product-scenes-section.tsx">
                    <SceneContainer activeScene={activeScene.id} data-sentry-element="SceneContainer" data-sentry-source-file="product-scenes-section.tsx" />
                </ProductUIThemeProvider>
            </ScrollSection.StickySection>
        </ScrollSection>;
}
type DesktopSceneProps = {
  preheader: string;
  title: string;
  description: string;
  id: Scene;
  fullHeight?: boolean;
  anchor?: boolean;
  button?: {
    href: string;
    textKey: string;
  };
  onEnter?: () => void;
  onEnterBack?: () => void;
};
function ProductSection({
  id,
  preheader,
  title,
  description,
  fullHeight,
  anchor,
  button,
  onEnter,
  onEnterBack
}: DesktopSceneProps) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'home-teams');
  return <Box className={fullHeight ? css.fullHeight : undefined} data-sentry-element="Box" data-sentry-component="ProductSection" data-sentry-source-file="product-scenes-section.tsx">
            <ScrollSection.Trigger onEnter={onEnter} onEnterBack={onEnterBack} data-sentry-element="unknown" data-sentry-source-file="product-scenes-section.tsx">
                <AnchorWrap shouldWrap={anchor} data-sentry-element="AnchorWrap" data-sentry-source-file="product-scenes-section.tsx">
                    <TextLockUp style={{
          '--text-lock-up-preheader-color': 'var(--productui-primary-theme-color)'
        }} size="lg" title={t(title)} preheader={t(preheader)} buttons={button ? <Button variant="tertiary" size="large" href={button.href} endIcon={ChevronRight} bleed="inline">
                                    {t(button.textKey)}
                                </Button> : null} data-sentry-element="TextLockUp" data-sentry-source-file="product-scenes-section.tsx">
                        {t(description)}
                    </TextLockUp>
                </AnchorWrap>

                <Hidden above="md" data-sentry-element="Hidden" data-sentry-source-file="product-scenes-section.tsx">
                    <SceneContainer className="mbs-32" activeScene={id} data-sentry-element="SceneContainer" data-sentry-source-file="product-scenes-section.tsx" />
                </Hidden>
            </ScrollSection.Trigger>
        </Box>;
}
function AnchorWrap({
  children,
  shouldWrap
}: {
  children: React.ReactNode;
  shouldWrap?: boolean;
}) {
  return shouldWrap ? <ScrollSection.StickyAnchor data-sentry-element="unknown" data-sentry-component="AnchorWrap" data-sentry-source-file="product-scenes-section.tsx">{children}</ScrollSection.StickyAnchor> : <>{children}</>;
}